import {
	JourneyExploreResult,
	JourneyFilterState,
	JourneyMeta,
} from '../../../models/journey-explorer/journey-explorer';

const KEY_PREFIX = 'journeyExplorer.cache.v2:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CachedSnapshot {
	filters: JourneyFilterState;
	data: JourneyExploreResult;
}

export interface JourneyCache {
	savedAt: number;
	meta: JourneyMeta | null;
	snapshot: CachedSnapshot | null;
}

const storageKey = (scope: string) => `${KEY_PREFIX}${scope}`;

export const readJourneyCache = (scope: string, now = Date.now()): JourneyCache | null => {
	try {
		const text = localStorage.getItem(storageKey(scope));
		if (!text) {
			return null;
		}
		const cache = JSON.parse(text) as JourneyCache;
		if (typeof cache.savedAt !== 'number' || now - cache.savedAt > MAX_AGE_MS) {
			localStorage.removeItem(storageKey(scope));
			return null;
		}
		return cache;
	} catch {
		return null;
	}
};

const writeJourneyCache = (scope: string, patch: Partial<Omit<JourneyCache, 'savedAt'>>, now = Date.now()) => {
	const current = readJourneyCache(scope, now);
	const next: JourneyCache = {
		meta: current?.meta ?? null,
		snapshot: current?.snapshot ?? null,
		...patch,
		savedAt: now,
	};
	try {
		localStorage.setItem(storageKey(scope), JSON.stringify(next));
	} catch {
		// Storage can be full or disabled; the page then simply loads without a head start.
		try {
			localStorage.removeItem(storageKey(scope));
		} catch {
			// Ignore: nothing more can be done without storage.
		}
	}
};

export const saveCachedMeta = (scope: string, meta: JourneyMeta, now?: number) =>
	writeJourneyCache(scope, { meta }, now);

export const saveCachedSnapshot = (scope: string, snapshot: CachedSnapshot, now?: number) =>
	writeJourneyCache(scope, { snapshot }, now);

export const clearJourneyCache = (scope: string) => {
	try {
		localStorage.removeItem(storageKey(scope));
	} catch {
		// Ignore: storage may be disabled.
	}
};

export const sameFilters = (a: JourneyFilterState | null | undefined, b: JourneyFilterState | null | undefined) =>
	!!a && !!b && JSON.stringify(a) === JSON.stringify(b);
